import React, { useState, useEffect } from 'react'
import { useQuery } from 'react-query'
import { useNavigate } from 'react-router-dom'
import {
  Users,
  FileText,
  DollarSign,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  Server,
  Activity,
  Zap,
  Workflow
} from 'lucide-react'
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { unifiedAdminApiClient as adminApi } from '../services/unified-admin-api-client'
import { adminApi as workflowApi } from '../services/api'
import { useSystemHealthUpdates, useGenerationUpdates, useMLSExportUpdates } from '../services/websocket'
import { DashboardStats, SystemHealth } from '../types'

export default function DashboardPage() {
  const [realtimeStats, setRealtimeStats] = useState<Partial<DashboardStats>>({})
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)

  // Fetch initial dashboard data
  const { data: dashboardData, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    () => adminApi.getDashboardStats(),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  )

  const { data: healthData, isLoading: healthLoading } = useQuery(
    'system-health',
    () => adminApi.getSystemHealth(),
    {
      refetchInterval: 10000, // Refetch every 10 seconds
    }
  )

  // WebSocket real-time updates
  useSystemHealthUpdates((data) => {
    setSystemHealth(data)
  })

  useGenerationUpdates((data) => {
    setRealtimeStats(prev => ({
      ...prev,
      total_generations: (prev.total_generations || 0) + 1,
      generations_today: (prev.generations_today || 0) + 1
    }))
  })

  useMLSExportUpdates((data) => {
    setRealtimeStats(prev => ({
      ...prev,
      mls_exports_today: (prev.mls_exports_today || 0) + 1
    }))
  })

  const stats = dashboardData?.data
  const health = healthData?.data || systemHealth

  // Combine API data with real-time updates
  const currentStats = {
    ...stats,
    ...realtimeStats
  }

  if (statsLoading || healthLoading) {
    return <DashboardSkeleton />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Real-time overview of your Virtual Revamp platform
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <SystemStatusIndicator health={health} />
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Users"
          value={currentStats?.total_users || 0}
          change={`+${currentStats?.users_registered_today || 0} today`}
          icon={Users}
          color="blue"
        />
        <StatCard
          title="Generations"
          value={currentStats?.total_generations || 0}
          change={`+${currentStats?.generations_today || 0} today`}
          icon={FileText}
          color="green"
        />
        <StatCard
          title="Revenue (Month)"
          value={`$${(currentStats?.revenue_month || 0).toLocaleString()}`}
          change={`$${(currentStats?.revenue_today || 0).toLocaleString()} today`}
          icon={DollarSign}
          color="purple"
        />
        <StatCard
          title="System Uptime"
          value={`${(currentStats?.system_uptime || 99.9).toFixed(1)}%`}
          change={`${(currentStats?.error_rate || 0.1).toFixed(2)}% error rate`}
          icon={Activity}
          color="orange"
        />
      </div>

      {/* System Health Overview */}
      {health && <SystemHealthOverview health={health} />}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <GenerationTrendsChart />
        <RevenueTrendsChart />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <RecentActivity />
        <TopUsers />
        <WorkflowStatus />
      </div>
    </div>
  )
}

interface StatCardProps {
  title: string
  value: string | number
  change: string
  icon: React.ComponentType<{ className?: string }>
  color: 'blue' | 'green' | 'purple' | 'orange'
}

function StatCard({ title, value, change, icon: Icon, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500'
  }

  return (
    <div className="stat-card">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className={`p-3 rounded-md ${colorClasses[color]}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="stat-value">{value}</dd>
          </dl>
        </div>
      </div>
      <div className="mt-4">
        <div className="text-sm text-gray-600">{change}</div>
      </div>
    </div>
  )
}

function SystemStatusIndicator({ health }: { health: SystemHealth | null }) {
  if (!health) {
    return (
      <div className="flex items-center space-x-2">
        <div className="h-3 w-3 bg-gray-400 rounded-full animate-pulse"></div>
        <span className="text-sm text-gray-500">Checking status...</span>
      </div>
    )
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
      case 'connected':
        return 'bg-green-500'
      case 'degraded':
      case 'slow':
        return 'bg-yellow-500'
      case 'down':
      case 'disconnected':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  const overallStatus = health.api_status === 'healthy' && 
                       health.database_status === 'connected' && 
                       health.redis_status === 'connected'
                       ? 'healthy' : 'degraded'

  return (
    <div className="flex items-center space-x-2">
      <div className={`h-3 w-3 rounded-full ${getStatusColor(overallStatus)}`}></div>
      <span className="text-sm text-gray-700 capitalize">
        System {overallStatus}
      </span>
    </div>
  )
}

function SystemHealthOverview({ health }: { health: SystemHealth }) {
  const services = [
    { name: 'API', status: health.api_status, icon: Server },
    { name: 'Database', status: health.database_status, icon: Server },
    { name: 'Redis', status: health.redis_status, icon: Zap },
  ]

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">System Health</h3>
      </div>
      <div className="card-body">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {services.map((service) => (
            <div key={service.name} className="flex items-center space-x-3">
              <service.icon className="h-5 w-5 text-gray-400" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{service.name}</span>
                  <span className={`badge ${
                    service.status === 'healthy' || service.status === 'connected' 
                      ? 'badge-success' 
                      : service.status === 'degraded' || service.status === 'slow'
                      ? 'badge-warning'
                      : 'badge-danger'
                  }`}>
                    {service.status}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Generation Queue</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Pending</span>
                <span className="font-medium">{health.generation_metrics.pending}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Processing</span>
                <span className="font-medium">{health.generation_metrics.processing}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Avg. Time</span>
                <span className="font-medium">{health.generation_metrics.avg_processing_time}s</span>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Workers</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Active Workers</span>
                <span className="font-medium">{health.celery_workers.active}/{health.celery_workers.total}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Queue Count</span>
                <span className="font-medium">{health.celery_workers.queues.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function GenerationTrendsChart() {
  const { data: trendsData, isLoading } = useQuery(
    'generation-trends',
    () => adminApi.getGenerationTrends(7),
    {
      refetchInterval: 60000, // Refetch every minute
    }
  )

  const data = trendsData?.data?.data || []

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Generation Trends</h3>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Area type="monotone" dataKey="generations" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.1} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function RevenueTrendsChart() {
  const { data: revenueData, isLoading } = useQuery(
    'revenue-trends',
    () => adminApi.getRevenueTrends(7),
    {
      refetchInterval: 60000, // Refetch every minute
    }
  )

  const data = revenueData?.data?.data || []

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Revenue Trends</h3>
      </div>
      <div className="card-body">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
            <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

function RecentActivity() {
  const { data: activityData, isLoading } = useQuery(
    'recent-activity',
    () => adminApi.getRecentActivity(10),
    {
      refetchInterval: 30000, // Refetch every 30 seconds
    }
  )

  const activities = activityData?.data?.data || []

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Recent Activity</h3>
      </div>
      <div className="card-body">
        <div className="space-y-4">
          {activities.map((activity: any) => (
            <div key={activity.id} className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="h-2 w-2 bg-blue-500 rounded-full mt-2"></div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-900">{activity.message}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function TopUsers() {
  const { data: usersData, isLoading } = useQuery(
    'top-users',
    () => adminApi.getTopUsers(5, 'month'),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  )

  const users = usersData?.data?.data || []

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Top Users This Month</h3>
      </div>
      <div className="card-body">
        <div className="space-y-4">
          {users.map((user: any, index: number) => (
            <div key={user.email} className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <span className="text-sm font-medium text-gray-500">#{index + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <div className="flex-shrink-0">
                <span className="text-sm font-medium text-gray-900">{user.generations}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function QuickActions() {
  const navigate = useNavigate()

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="text-lg font-medium">Quick Actions</h3>
      </div>
      <div className="card-body">
        <div className="space-y-3">
          <button 
            className="btn-primary w-full text-left"
            onClick={() => handleNavigation('/users')}
          >
            View All Users
          </button>
          <button 
            className="btn-secondary w-full text-left"
            onClick={() => handleNavigation('/generations')}
          >
            Monitor Generations
          </button>
          <button 
            className="btn-secondary w-full text-left"
            onClick={() => handleNavigation('/mls')}
          >
            Check MLS Exports
          </button>
          <button 
            className="btn-secondary w-full text-left"
            onClick={() => handleNavigation('/system')}
          >
            System Health
          </button>
        </div>
      </div>
    </div>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-1/4"></div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="card">
            <div className="card-body">
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="card">
            <div className="card-body">
              <div className="h-64 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function WorkflowStatus() {
  const [workflowOverview, setWorkflowOverview] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadWorkflowStatus = async () => {
      try {
        const response = await workflowApi.getWorkflowsOverview()
        if (response.success) {
          setWorkflowOverview(response.data)
        }
      } catch (error) {
        console.error('Error loading workflow status:', error)
      } finally {
        setLoading(false)
      }
    }

    loadWorkflowStatus()
  }, [])

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'text-green-600'
      case 'testing':
        return 'text-yellow-600'
      case 'failed':
        return 'text-red-600'
      default:
        return 'text-gray-600'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4" />
      case 'testing':
        return <Clock className="h-4 w-4" />
      case 'failed':
        return <AlertTriangle className="h-4 w-4" />
      default:
        return <Activity className="h-4 w-4" />
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Workflow Status</h3>
        <Workflow className="h-5 w-5 text-gray-400" />
      </div>
      
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      ) : (
        <div className="space-y-3">
          {workflowOverview.map((tier) => (
            <div key={tier.quality_tier} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-sm capitalize">{tier.quality_tier}</div>
                <div className="text-xs text-gray-500">
                  {tier.active_workflow ? tier.active_workflow.name : 'No active workflow'}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {tier.active_workflow && (
                  <span className={`flex items-center space-x-1 text-xs ${getStatusColor(tier.active_workflow.status)}`}>
                    {getStatusIcon(tier.active_workflow.status)}
                    <span className="capitalize">{tier.active_workflow.status}</span>
                  </span>
                )}
                <span className="text-xs text-gray-500">
                  {tier.credits} credits
                </span>
              </div>
            </div>
          ))}
          
          {workflowOverview.length === 0 && (
            <div className="text-center py-4 text-gray-500 text-sm">
              No workflows configured
            </div>
          )}
          
          <div className="pt-2 border-t border-gray-200">
            <a
              href="/workflows"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Manage Workflows →
            </a>
          </div>
        </div>
      )}
    </div>
  )
}